import { useRef, useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EmailEditor, { EditorRef } from "react-email-editor";
import SafeArea from "components/safe-area";
import FlatContainer from "components/container/flat";
import { Button, OutlineButton } from "components/button";
import { Input } from "components/input";
import { IMergeField } from "../types/IEmailTemplate";
import { useTemplateEditor } from "./hooks/useTemplateEditor";

const GSM7_SEGMENT_SIZE = 160;

const TemplateEditor = () => {
  const { t } = useTranslation();
  const tBase = "views.outreach.templates.editor";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const history = useHistory();
  const emailEditorRef = useRef<EditorRef>(null);
  const smsTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);

  const {
    name,
    setName,
    subject,
    setSubject,
    templateType,
    setTemplateType,
    messageText,
    setMessageText,
    isLoading,
    isSaving,
    mergeFields,
    mergeTags,
    handleSave,
    errors,
    touched,
    setTouched,
    template,
    isEditing,
  } = useTemplateEditor(emailEditorRef);

  const smsSegmentCount = Math.max(1, Math.ceil(messageText.length / GSM7_SEGMENT_SIZE));

  const insertMergeField = useCallback(
    (fieldKey: string) => {
      const tag = `{{${fieldKey}}}`;
      const textarea = smsTextareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newText =
          messageText.substring(0, start) + tag + messageText.substring(end);
        setMessageText(newText);
        requestAnimationFrame(() => {
          textarea.focus();
          const cursorPos = start + tag.length;
          textarea.setSelectionRange(cursorPos, cursorPos);
        });
      } else {
        setMessageText(messageText + tag);
      }
    },
    [messageText, setMessageText]
  );

  const onEditorReady = () => {
    if (isEditing && template?.designJson) {
      emailEditorRef.current?.editor?.loadDesign(
        template.designJson as Parameters<
          NonNullable<typeof emailEditorRef.current>["editor"]["loadDesign"]
        >[0]
      );
    }
  };

  if (isLoading) {
    return (
      <SafeArea>
        <div className="py-12 flex items-center justify-center">
          <p className="text-gray-600 text-lg">{tr("loading")}</p>
        </div>
      </SafeArea>
    );
  }

  return (
    <SafeArea>
      {/* Header */}
      <FlatContainer
        title={
          <h2>{isEditing ? tr("editTitle") : tr("newTitle")}</h2>
        }
        buttons={[
          <OutlineButton
            key="back"
            size="small"
            onClick={() => history.push("/outreach/templates")}
          >
            {tr("cancel")}
          </OutlineButton>,
        ]}
      />

      {/* Template Type Toggle */}
      <div className="py-4 border-b">
        {!isEditing ? (
          <div className="flex items-center gap-2 mb-4">
            <label className="text-sm font-semibold mr-2">{tr("typeLabel")}:</label>
            <button
              type="button"
              className={`px-4 py-1.5 rounded-l text-sm font-medium border transition-colors ${
                templateType === "email"
                  ? "bg-[#ff4f00] text-white border-[#ff4f00]"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
              onClick={() => setTemplateType("email")}
            >
              Email
            </button>
            <button
              type="button"
              className={`px-4 py-1.5 rounded-r text-sm font-medium border border-l-0 transition-colors ${
                templateType === "sms"
                  ? "bg-[#ff4f00] text-white border-[#ff4f00]"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
              onClick={() => setTemplateType("sms")}
            >
              SMS
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-4">
            <label className="text-sm font-semibold mr-2">{tr("typeLabel")}:</label>
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 uppercase">
              {templateType}
            </span>
          </div>
        )}

        {/* Form fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name input */}
          <Input
            label={tr("nameLabel")}
            value={name}
            onChange={(e: any) => setName(e.target.value)}
            onBlur={() =>
              setTouched((prev) => ({ ...prev, name: true }))
            }
            placeholder={tr("namePlaceholder")}
            error={errors.name && touched.name ? errors.name : ""}
          />

          {/* Subject input — email only */}
          {templateType === "email" && (
            <Input
              label={tr("subjectLabel")}
              value={subject}
              onChange={(e: any) => setSubject(e.target.value)}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, subject: true }))
              }
              placeholder={tr("subjectPlaceholder")}
              error={errors.subject && touched.subject ? errors.subject : ""}
            />
          )}
        </div>
      </div>

      {/* SMS Message Composer */}
      {templateType === "sms" && (
        <div className="mt-4">
          <div className="border rounded p-4">
            <label className="block text-sm font-semibold mb-2">
              {tr("messageLabel")}
            </label>

            {/* Merge field insert buttons */}
            {mergeFields.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className="text-xs text-gray-500 self-center mr-1">
                  {tr("insertMergeField")}:
                </span>
                {mergeFields.map((field: IMergeField) => (
                  <button
                    key={field.key}
                    type="button"
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 hover:bg-[#ff4f00] hover:text-white hover:border-[#ff4f00] transition-colors cursor-pointer"
                    onClick={() => insertMergeField(field.key)}
                    title={field.description || field.label}
                  >
                    {field.label}
                  </button>
                ))}
              </div>
            )}

            <textarea
              ref={smsTextareaRef}
              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:border-[#ff4f00] resize-y font-mono text-sm"
              rows={6}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, messageText: true }))
              }
              placeholder={tr("messagePlaceholder")}
            />

            {/* Character count and segment indicator */}
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-500">
                  {messageText.length} / {GSM7_SEGMENT_SIZE} {tr("characters")}
                </span>
                <span className="text-xs text-gray-500">
                  {smsSegmentCount} {smsSegmentCount === 1 ? tr("segment") : tr("segments")}
                  {smsSegmentCount > 1 && (
                    <span className="text-gray-400 ml-1">
                      ({GSM7_SEGMENT_SIZE} {tr("charsPerSegment")})
                    </span>
                  )}
                </span>
              </div>
              {errors.messageText && touched.messageText && (
                <span className="text-xs text-red-500">{errors.messageText}</span>
              )}
            </div>

            <p className="mt-3 text-xs text-gray-400 italic">
              {tr("stopNotice")}
            </p>
          </div>
        </div>
      )}

      {/* Email Editor */}
      {templateType === "email" && (
        <div className="mt-4">
          <div className="flex items-center justify-end mb-2">
            <OutlineButton
              size="small"
              onClick={() => {
                const editor = emailEditorRef.current?.editor;
                if (!editor) return;
                if (isPreviewing) {
                  editor.hidePreview();
                  setIsPreviewing(false);
                } else {
                  editor.showPreview("desktop");
                  setIsPreviewing(true);
                }
              }}
            >
              {isPreviewing ? tr("backToEditor") : tr("preview")}
            </OutlineButton>
          </div>
          <div
            className="border rounded overflow-hidden"
            style={{ height: 600 }}
          >
            <EmailEditor
              ref={emailEditorRef}
              onReady={onEditorReady}
              minHeight="600px"
              options={{
                mergeTags,
                features: { textEditor: { tables: true } },
              }}
            />
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-3 py-4">
        <OutlineButton
          onClick={() => history.push("/outreach/templates")}
          disabled={isSaving}
        >
          {tr("cancel")}
        </OutlineButton>
        <Button
          primary
          onClick={handleSave}
          disabled={isSaving}
          isLoading={isSaving}
        >
          {isSaving ? tr("saving") : tr("save")}
        </Button>
      </div>
    </SafeArea>
  );
};

export default TemplateEditor;
